import React, { FC, useCallback, useState, useEffect } from 'react';

import { ValueType } from 'react-select';
import CreatableSelect from 'react-select/creatable';

import { Tag, TagOption } from 'types';

import { getTagsByType } from 'features/tags/api';
import { requestArtwork, putArtworkCategories } from 'features/artworks/api';

import { DropdownIndicator } from './components/DropdownIndicator';
import { ClearIndicator } from './components/ClearIndicator';
import { MultiValueRemove } from './components/MultiValueRemove';

const formatArtworkCategoryToOption = (name: Tag['name']): TagOption => ({
  value: name,
  label: name,
});
const formatTagToOption = ({ name }: Tag): TagOption => ({
  value: name,
  label: name,
});
const formatOptionToString = ({ value }: TagOption): string => value;

type Props = {
  artworkId: number;
  limit?: number;
  limitReachedMessage?: string;
  inputId?: string;
};

export const ArtworkCategoryEditor: FC<Props> = ({
  artworkId,
  limit,
  limitReachedMessage,
  inputId,
}) => {
  const [isLoading, setLoadingState] = useState<boolean>(false);

  const [options, setOptions] = useState<TagOption[]>([]);
  const [values, setValues] = useState<ValueType<TagOption, true>>();

  const updateArtworkCategories = useCallback(
    (selected: ValueType<TagOption, true> = []) => {
      setLoadingState(true);
      setValues(selected);
      const options = selected.map(formatOptionToString);
      putArtworkCategories(artworkId, options).finally(() => setLoadingState(false));
    },
    [artworkId],
  );

  useEffect(() => {
    if (artworkId) {
      setLoadingState(true);
      const getData = async () => {
        const artwork = await requestArtwork(artworkId);
        const options = await getTagsByType('category');
        setValues(artwork?.categories?.map(formatArtworkCategoryToOption));
        setOptions(options?.map(formatTagToOption));
        setLoadingState(false);
      };
      getData();
    }
  }, []);

  return (
    <CreatableSelect
      id={inputId}
      isMulti
      isLoading={isLoading}
      onChange={updateArtworkCategories}
      placeholder={
        <span style={{ paddingLeft: '6px' }}>{isLoading ? 'Loading...' : 'Select...'}</span>
      }
      value={values}
      options={limit && (values || []).length >= limit ? [] : options}
      components={{
        DropdownIndicator,
        ClearIndicator,
        MultiValueRemove,
      }}
      theme={(theme) => ({
        ...theme,
        borderRadius: 0,
        borderWidth: '1px',
        colors: {
          ...theme.colors,
          primary: '#E6E6E6',
          primary25: '#d9f6f0',
          primary50: '#00c49d',
        },
      })}
      styles={{
        input: (style) => ({
          ...style,
          input: {
            marginBottom: '0 !important',
            height: '20px',
          },
        }),
        valueContainer: (style) => ({
          ...style,
          padding: '2px',
        }),
        container: (style) => ({
          ...style,
          minWidth: '234px',
          borderWidth: '1px',
          borderColor: '#dcdcdf',
        }),
        control: (style) => ({
          ...style,
          boxShadow: 'none',
          alignContent: 'center',
          ':hover': {
            borderColor: '#99999c',
          },
        }),
        multiValue: (style) => ({
          ...style,
          borderRadius: '2px',
        }),
        multiValueRemove: (style) => ({
          ...style,
          fontSize: '10px',
          '& :before': {
            lineHeight: 'unset !important',
          },
          ':hover': {
            backgroundColor: '#F2F2F2',
            borderTopRightRadius: '2px',
            borderBottomRightRadius: '2px',
          },
        }),
        multiValueLabel: (style) => ({
          ...style,
          marginBottom: '-2px',
          fontSize: '14px',
          paddingRight: '6px',
          borderRight: '1px solid #fff',
        }),
        dropdownIndicator: (style) => ({
          ...style,
          padding: '6px',
          '& :before': {
            lineHeight: 'unset !important',
          },
        }),
        clearIndicator: (style) => ({
          ...style,
          padding: '6px',
          '& :before': {
            lineHeight: 'unset !important',
          },
        }),
      }}
      noOptionsMessage={() => {
        return limit && (values || [])?.length >= limit
          ? limitReachedMessage || 'Limit reached'
          : 'No options';
      }}
    />
  );
};
